/** Invoice Detail Report — monthly trade list for Admin / Super User. */

import {
  buildCustomReportAgentOptions,
  buildCustomReportTradeOptions,
  formatIsoToMMDDYYYY,
  formatMMDDYYYYToIso,
} from "./customReportUtils";

export const INVOICE_DETAIL_TYPES = [
  { value: "Sale", label: "Sale" },
  { value: "Lease", label: "Lease" },
];

export const INVOICE_DETAIL_STATUSES = [
  { value: "Closed", label: "Closed" },
  { value: "SOLD", label: "SOLD" },
  { value: "Leased", label: "Leased" },
  { value: "FELL THRU", label: "FELL THRU" },
];

/** Optional filter when generating the report (not a row value). */
export const INVOICE_DETAIL_STATUS_FILTER = [
  { value: "", label: "Any status" },
  ...INVOICE_DETAIL_STATUSES,
];

export const INVOICE_DETAIL_COLUMNS = [
  { key: "reportDate", label: "Date", edit: "select" },
  { key: "tradeNumber", label: "Trade #", edit: "select" },
  { key: "agentName", label: "Agent Name", edit: "select" },
  { key: "propertyAddress", label: "Property Address", edit: "text" },
  { key: "dealType", label: "Type", edit: "select" },
  { key: "status", label: "Status", edit: "select" },
];

function clean(value) {
  return String(value ?? "").trim();
}

function personFullName(p) {
  if (!p) return "";
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || clean(p.name) || clean(p.companyName);
}

function getTradeEntryIso(trade) {
  const k = trade?.keyInfo || {};
  return clean(k.entryDate || trade?.entryDate || "");
}

function tradeInEntryPeriod(trade, fromIso, toIso) {
  const entry = getTradeEntryIso(trade);
  if (!entry) return false;
  if (fromIso && entry < fromIso) return false;
  if (toIso && entry > toIso) return false;
  return true;
}

function getPrimaryAgentName(trade) {
  const list = trade?.agentCommissionList || [];
  if (!list.length) return "";
  const lead =
    list.find((a) => /lead|listing|yes/i.test(String(a.agentLead || ""))) ||
    list[0];
  return clean(lead?.agentName || "");
}

export function normalizeInvoiceDetailStatus(value) {
  const raw = clean(value);
  if (!raw) return "Closed";
  const upper = raw.toUpperCase();
  if (/sold/i.test(upper)) return "SOLD";
  if (/lease/i.test(upper)) return "Leased";
  if (/fall|thru|through|term|cancel/i.test(upper)) return "FELL THRU";
  if (/closed|final/i.test(upper)) return "Closed";
  const match = INVOICE_DETAIL_STATUSES.find(
    (o) => o.value.toUpperCase() === upper || o.label.toUpperCase() === upper,
  );
  return match?.value || raw;
}

export function normalizeInvoiceDetailDealType(value) {
  const raw = clean(value);
  if (/lease/i.test(raw)) return "Lease";
  if (/sale|purchase/i.test(raw)) return "Sale";
  return raw || "Sale";
}

export function propertyAddressFromTrade(trade) {
  const k = trade?.keyInfo || {};
  const street = [k.streetNumber, k.streetName].filter(Boolean).join(" ");
  const withUnit = k.unit
    ? street
      ? `${k.unit}-${street}`
      : clean(k.unit)
    : street;
  const cityLine = [withUnit, k.city].filter(Boolean).join(", ");
  return cityLine || clean(k.propertyAddress) || "";
}

/** First and last calendar day (YYYY-MM-DD) for an `<input type="month">` value. */
export function monthRangeFromPicker(monthValue) {
  if (!monthValue || !/^\d{4}-\d{2}$/.test(monthValue)) {
    return { fromIso: "", toIso: "", monthLabel: "" };
  }
  const [y, m] = monthValue.split("-").map(Number);
  const fromIso = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const toIso = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return {
    fromIso,
    toIso,
    monthLabel: `${months[m - 1]} ${y}`,
  };
}

function formatIsoLabel(iso) {
  if (!iso) return "";
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m) return iso;
  return `${months[m - 1]} ${String(d || 1).padStart(2, "0")}, ${y}`;
}

export function formatInvoiceDetailPeriodLabel(fromIso, toIso) {
  if (fromIso && toIso) return `${formatIsoLabel(fromIso)} to ${formatIsoLabel(toIso)}`;
  if (fromIso) return `${formatIsoLabel(fromIso)} to Date`;
  if (toIso) return `Through ${formatIsoLabel(toIso)}`;
  return "";
}

function tradeMatchesStatusFilter(trade, statusFilter) {
  if (!statusFilter) return true;
  const k = trade?.keyInfo || {};
  const raw = k.status || trade?.status || "";
  const normalized = normalizeInvoiceDetailStatus(raw);
  return (
    normalized === statusFilter ||
    String(normalized).toUpperCase() === String(statusFilter).toUpperCase()
  );
}

export function buildInvoiceDetailRow(trade) {
  const k = trade?.keyInfo || {};
  const reportIso = getTradeEntryIso(trade);
  return {
    rowId: String(trade?._id || trade?.tradeNumber || Math.random()),
    tradeId: trade?._id || "",
    tradeNumber: clean(trade?.tradeNumber || k.tradeNumber),
    reportDate: formatIsoToMMDDYYYY(reportIso),
    reportDateIso: reportIso,
    agentName: getPrimaryAgentName(trade),
    propertyAddress: propertyAddressFromTrade(trade),
    dealType: normalizeInvoiceDetailDealType(k.dealType || k.type),
    status: normalizeInvoiceDetailStatus(k.status || trade?.status),
    dirty: false,
  };
}

export function buildInvoiceDetailRows(
  trades,
  fromIso,
  toIso,
  statusFilter = "",
) {
  if (!fromIso && !toIso) return [];
  return (trades || [])
    .filter((t) => tradeInEntryPeriod(t, fromIso, toIso))
    .filter((t) => tradeMatchesStatusFilter(t, statusFilter))
    .map(buildInvoiceDetailRow)
    .sort((a, b) => {
      const da = a.reportDateIso || "";
      const db = b.reportDateIso || "";
      if (da !== db) return da.localeCompare(db);
      const na = parseInt(String(a.tradeNumber).match(/\d+/)?.[0] || "0", 10);
      const nb = parseInt(String(b.tradeNumber).match(/\d+/)?.[0] || "0", 10);
      return na - nb;
    });
}

export function buildInvoiceDetailDateOptions(rows) {
  const seen = new Set();
  return (rows || [])
    .map((r) => clean(r.reportDate))
    .filter((d) => {
      if (!d || seen.has(d)) return false;
      seen.add(d);
      return true;
    })
    .sort()
    .map((value) => ({ value, label: value }));
}

export {
  buildCustomReportTradeOptions as buildInvoiceDetailTradeOptions,
  buildCustomReportAgentOptions as buildInvoiceDetailAgentOptions,
};

export function getInvoiceDetailSelectOptions(key, row, tradeOptions, agentOptions, dateOptions) {
  if (key === "tradeNumber") return tradeOptions || [];
  if (key === "agentName") return agentOptions || [];
  if (key === "reportDate") {
    const opts = [...(dateOptions || [])];
    if (row.reportDate && !opts.some((o) => o.value === row.reportDate)) {
      opts.unshift({ value: row.reportDate, label: row.reportDate });
    }
    return opts;
  }
  if (key === "dealType") return INVOICE_DETAIL_TYPES;
  if (key === "status") return INVOICE_DETAIL_STATUSES;
  return [];
}

export function invoiceDetailRowToTradePatch(row) {
  const entryIso =
    formatMMDDYYYYToIso(row.reportDate) || row.reportDateIso || "";
  return {
    keyInfo: {
      dealType: row.dealType || "",
      status: row.status || "",
      entryDate: entryIso,
      propertyAddress: row.propertyAddress || "",
    },
  };
}
